import { Route, Router } from '@axa-fr-axion/router';
import { ActionsLayout, MainLayout, ScrollToTopOfLayout } from '@axa-fr-axion/layouts';
import { LoaderPage } from '@axa-fr-axion/loader';
import { FunctionalErrorResponse } from '@axa-fr-axion/restful';
import { Dossier } from '@axa-fr-axion/user-journey-services';
import '@axafr-btc-ds/colors/theme-vars.css';
import { NavigationPath, StepNavigationPath, TechNavigationPath, ValidityStatus } from 'interfaces';
import { lazy, useCallback, useEffect, useReducer, useState } from 'react';
import { journeyDataMocks } from 'mocks/data-mocks';
import { sendTagEventNavigation, shouldSendTagEventNavigation } from 'services/tag';
import { AppOptions, AppProps } from 'App.interface';
import { JourneyData, JourneyDataValidityKeys, JourneyDataValue, JourneyInitialData, JourneyReducer } from 'data';
import { journeyReducer } from 'App.reducer';
import { chooseEndStep, DEFAULT_OPTIONS, folderReady, getInitialStep, initJourneyDataWithOrigin } from 'App.utils';
import upperFirst from 'lodash.upperfirst';
import { isAcceptedValidityStatus } from 'utils';
import lazyWithPreload from 'react-lazy-with-preload';
import { Stepper } from '@axa-fr-axion/stepper';
import { FinalButton, HelpButton, NextButton, PreviousButton, RenderIf } from 'components';
import { selectPreset } from 'presets';
import { getNextStepFrom, getPreviousStepFrom } from 'router/App.router.utils';
import styles from './App.module.css';
import { Typography } from '@axafr-btc-ds/typography';
import { Link } from '@axafr-btc-ds/link';

const Scenarios = lazy(() => import('features/scenarios/Scenarios'));
const Initialization = lazy(() => import('features/initialization/Initialization'));
export const Identity = lazyWithPreload(() => import('features/contact/identity/Identity'));
export const HomeAddress = lazyWithPreload(() => import('features/contact/home-address/HomeAddress'));
// Optional step - Choose Advisor
export const PreChooseAdvisor = lazyWithPreload(() => import('features/pre-choose-advisor/PreChooseAdvisor'));
export const ChooseAdvisor = lazyWithPreload(() => import('features/choose-advisor/ChooseAdvisor'));
const PreConfirmation = lazy(() => import('features/pre-confirmation/PreConfirmation'));
// Final step
const End = lazy(() => import('features/end/End'));
export const LogementType = lazyWithPreload(() => import('features/logement-type/LogementType'));
export const LogementNombrePieces = lazyWithPreload(() => import('features/logement-nombre-pieces/LogementNombrePieces'));
export const LocataireType = lazyWithPreload(() => import('features/locataire-type/LocataireType'));
export const LogementResidencePrincipale = lazyWithPreload(() => import('features/logement-residence-principale/LogementResidencePrincipale'));
export const LogementAdresse = lazyWithPreload(() => import('features/logement-adresse/LogementAdresse'));
export const LogementDebutAssurance = lazyWithPreload(() => import('features/logement-debut-assurance/LogementDebutAssurance'));
// Technical error
const TechnicalError = lazyWithPreload(() => import('features/technical-error/TechnicalError'));

const ACCEPTER_D_ETRE_RECONTACTER = 'Accepter d\'être recontacté';
const ACCEPTER_D_ETRE_CONTACTER = 'Accepter d\'être contacté';

function App({ data, onScenarioChange, options, path }: AppProps): JSX.Element {
    const _options: AppOptions = {
        ...DEFAULT_OPTIONS,
        ...options,
    };

    const csiPhoneNumberHelp = _options.editorialSpecific?.csiPhoneNumberHelp ?? '';

    const preset = selectPreset(_options.config?.preset);
    const initialStep = getInitialStep(preset);
    const [ step, setStep ] = useState<NavigationPath>(path ?? initialStep);
    const [ journeyData, dispatchJourneyData ] =
        useReducer<JourneyReducer, JourneyInitialData>(
            journeyReducer,
            data,
            initJourneyDataWithOrigin(),
        );

    const goToStep = (newStep: NavigationPath) => {
        setStep(newStep);
        if (shouldSendTagEventNavigation(preset.type, newStep)) {
            void sendTagEventNavigation(newStep, journeyData, options);
        }
    };

    const goToNextStep = (overrides?: Pick<Partial<JourneyData>, 'folder'>) => {
        const newStep = getNextStepFrom(step, preset, {
            ...journeyData,
            ...overrides,
        });
        if (newStep) {
            goToStep(newStep);
        }
    };

    const goToNextStepWithoutTag = (overrides?: Pick<Partial<JourneyData>, 'folder'>) => {
        const newStep = getNextStepFrom(step, preset, {
            ...journeyData,
            ...overrides,
        });
        if (newStep) {
            setStep(newStep);
        }
    };

    const backToPreviousStep = () => {
        const newStep = getPreviousStepFrom(step, preset, journeyData);
        if (newStep) {
            goToStep(newStep);
        }
    };

    useEffect(() => {
        if (journeyData.folder.lead?.hash) {
            if (shouldSendTagEventNavigation(preset.type, step)) {
                void sendTagEventNavigation(step, journeyData, options);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ journeyData.folder.lead?.hash ]);

    const handleInitializationSuccess = (folder: Dossier): void => {
        dispatchJourneyData({ type: 'INITIALIZE', folder });
    };

    const handlePreContactInitializationSuccess = (folder: Dossier): void => {
        dispatchJourneyData({ type: 'INITIALIZE', folder });
        goToNextStepWithoutTag();
    };

    const handleTechnicalError = (): void => {
        goToStep('Error');
    };

    const handleChange = useCallback((type: keyof Omit<JourneyData, 'folder'>) => {
        const validityKey = `is${ upperFirst(type) }Valid` as JourneyDataValidityKeys;
        return (newData: Omit<JourneyDataValue, 'folder'>, validityStatus: ValidityStatus): void => {
            dispatchJourneyData({
                type,
                payload: {
                    [ type ]: newData,
                    [ validityKey ]: isAcceptedValidityStatus(validityStatus),
                },
            });
        };
    }, [ dispatchJourneyData ]);

    const handleFunctionalError = (error?: FunctionalErrorResponse): void => {
        goToStep(chooseEndStep(error?.status?.funnel));
    };

    const prefillHomeAddressAndGoToNextStep = (): void => {
        goToNextStep();
        dispatchJourneyData({
            type: 'PREFILL_HOME_ADDRESS',
            logementAdresse: journeyData.logementAdresse,
        });
    };

    const shouldDisplayHelpButton: boolean = preset.screens.contact.displayHelpButton;

    const shouldDisplayPreviousButton: boolean = preset.screens.contact.displayPreviousButton;

    const LoaderComponent = <LoaderPage title="Veuillez patienter, nous chargeons la prochaine étape..." />;

    const FinalButtonValue = preset.type === 'DevisPP' ? ACCEPTER_D_ETRE_RECONTACTER : ACCEPTER_D_ETRE_CONTACTER;

    return (
        <MainLayout>
            <ScrollToTopOfLayout path={ step }>
                <Stepper<NavigationPath>
                    steps={ preset.stepper.steps }
                    data={ preset.stepper.data }
                    path={ step }
                />
            </ScrollToTopOfLayout>
            <section aria-label="Parcours Générique">
                <Router<NavigationPath>
                    current={ step }
                >
                    <Route<StepNavigationPath> path="LogementType" fallback={ LoaderComponent }>
                        <RenderIf value={ !journeyData.folder.id }>
                            <Initialization
                                data={ {
                                    origine: _options.origine ?? {},
                                    proprietes: _options.proprietes,
                                } }
                                onSuccess={ handleInitializationSuccess }
                                onTechnicalError={ handleTechnicalError }
                            />
                        </RenderIf>
                        <LogementType
                            data={ journeyData.logementType }
                            onChange={ handleChange('logementType') }
                        />
                        <div className={ styles.navButtonContainer }>
                            <ActionsLayout>
                                <NextButton
                                    isDisabled={ !journeyData.isLogementTypeValid || !folderReady(journeyData) }
                                    nextScreens={ [ LogementNombrePieces ] }
                                    onClick={ () => goToNextStep() }
                                />
                                <HelpButton phoneNumber={ csiPhoneNumberHelp } />
                            </ActionsLayout>
                        </div>
                    </Route>
                    <Route<StepNavigationPath> path="LogementNombrePieces" fallback={ LoaderComponent }>
                        <LogementNombrePieces
                            data={ journeyData.logementNombrePieces }
                            onChange={ handleChange('logementNombrePieces') }
                        />
                        <ActionsLayout>
                            <PreviousButton onClick={ () => backToPreviousStep() } />
                            <NextButton
                                isDisabled={ !journeyData.isLogementNombrePiecesValid }
                                nextScreens={ [ LocataireType ] }
                                onClick={ () => goToNextStep() }
                            />
                            <HelpButton phoneNumber={ csiPhoneNumberHelp } />
                        </ActionsLayout>
                    </Route>
                    <Route<StepNavigationPath> path="LocataireType" fallback={ LoaderComponent }>
                        <LocataireType
                            data={ journeyData.locataireType }
                            onChange={ handleChange('locataireType') }
                        />
                        <ActionsLayout>
                            <PreviousButton onClick={ () => backToPreviousStep() } />
                            <NextButton
                                isDisabled={ !journeyData.isLocataireTypeValid }
                                nextScreens={ [ LogementResidencePrincipale ] }
                                onClick={ () => goToNextStep() }
                            />
                            <HelpButton phoneNumber={ csiPhoneNumberHelp } />
                        </ActionsLayout>
                    </Route>
                    <Route<StepNavigationPath> path="LogementResidencePrincipale" fallback={ LoaderComponent }>
                        <LogementResidencePrincipale
                            data={ journeyData.logementResidencePrincipale }
                            onChange={ handleChange('logementResidencePrincipale') }
                        />
                        <ActionsLayout>
                            <PreviousButton onClick={ () => backToPreviousStep() } />
                            <NextButton
                                isDisabled={ !journeyData.isLogementResidencePrincipaleValid }
                                nextScreens={ [ LogementAdresse ] }
                                onClick={ () => goToNextStep() }
                            />
                            <HelpButton phoneNumber={ csiPhoneNumberHelp } />
                        </ActionsLayout>
                    </Route>
                    <Route<StepNavigationPath> path="LogementAdresse" fallback={ LoaderComponent }>
                        <LogementAdresse
                            data={ journeyData.logementAdresse }
                            onChange={ handleChange('logementAdresse') }
                        />
                        <ActionsLayout>
                            <PreviousButton onClick={ () => backToPreviousStep() } />
                            <NextButton
                                isDisabled={ !journeyData.isLogementAdresseValid }
                                nextScreens={ [ LogementDebutAssurance ] }
                                onClick={ () => prefillHomeAddressAndGoToNextStep() }
                            />
                            <HelpButton phoneNumber={ csiPhoneNumberHelp } />
                        </ActionsLayout>
                    </Route>
                    <Route<StepNavigationPath> path="LogementDebutAssurance" fallback={ LoaderComponent }>
                        <LogementDebutAssurance
                            data={ journeyData.logementDebutAssurance }
                            onChange={ handleChange('logementDebutAssurance') }
                        />
                        <ActionsLayout>
                            <PreviousButton onClick={ () => backToPreviousStep() } />
                            <NextButton
                                isDisabled={ !journeyData.isLogementDebutAssuranceValid }
                                nextScreens={ [ HomeAddress, Identity ] }
                                onClick={ () => goToNextStep() }
                            />
                            <HelpButton phoneNumber={ csiPhoneNumberHelp } />
                        </ActionsLayout>
                    </Route>
                    <Route<TechNavigationPath> path="PreContact" fallback={ LoaderComponent }>
                        <RenderIf value={ !journeyData.folder.id && preset?.type === 'DevisPP' }>
                            <Initialization
                                data={ {
                                    origine: _options.origine ?? {},
                                    proprietes: _options.proprietes,
                                } }
                                onSuccess={ handlePreContactInitializationSuccess }
                                onTechnicalError={ handleTechnicalError }
                            />
                            { LoaderComponent }
                        </RenderIf>
                    </Route>
                    <Route<StepNavigationPath> path="Contact" fallback={ LoaderComponent }>
                        <Identity
                            data={ journeyData.identity }
                            onChange={ handleChange('identity') }
                            preset={ preset.type }
                        />
                        <HomeAddress
                            data={ journeyData.homeAddress }
                            onChange={ handleChange('homeAddress') }
                            preset={ preset.type }
                        />
                        <RenderIf value={ preset?.type === 'DevisMRH' }>
                            <p className={ styles.contactText }>
                                Un conseiller va vous recontacter pour répondre au mieux à votre demande et
                                vous accompagner dans les prochaines étapes de souscription.
                            </p>
                        </RenderIf>
                        <RenderIf value={ preset?.type === 'DevisPP' }>
                            <p className={ styles.contactText }>
                                Notre conseiller AXA sera ravi de vous recontacter rapidement pour répondre
                                à votre demande.
                            </p>
                        </RenderIf>
                        <p className={ styles.mandatory }>
                            * Champs obligatoires
                        </p>

                        <ActionsLayout>
                            <RenderIf value={ shouldDisplayPreviousButton }>
                                <PreviousButton onClick={ () => backToPreviousStep() } />
                            </RenderIf>
                            <FinalButton
                                isDisabled={
                                    !journeyData.isIdentityValid ||
                                        !journeyData.isHomeAddressValid
                                }
                                nextScreens={ [ PreChooseAdvisor ] }
                                onClick={ () => goToNextStep() }
                            >
                                { FinalButtonValue }
                            </FinalButton>
                            <RenderIf value={ shouldDisplayHelpButton }>
                                <HelpButton phoneNumber={ csiPhoneNumberHelp } />
                            </RenderIf>
                        </ActionsLayout>
                    </Route>
                    <Route<TechNavigationPath> path="PreChooseAdvisor" fallback={ LoaderComponent }>
                        <PreChooseAdvisor
                            journeyData={ journeyData }
                            onSuccess={ (folder) => {
                                dispatchJourneyData({ type: 'SET_DEFAULT_ADVISOR', folder });
                                goToNextStep({ folder });
                            } }
                            onFunctionalError={ handleFunctionalError }
                            onTechnicalError={ handleTechnicalError }
                        />
                        { LoaderComponent }
                    </Route>
                    <Route<NavigationPath> path="ChooseAdvisor" fallback={ LoaderComponent }>
                        <ChooseAdvisor
                            data={ journeyData.selectedAdvisor }
                            onChange={ (advisor) => {
                                dispatchJourneyData({ type: 'SELECT_ADVISOR', advisor });
                            } }
                            folder={ journeyData.folder }
                        />
                        <ActionsLayout>
                            <PreviousButton onClick={ () => backToPreviousStep() } />
                            <NextButton
                                isDisabled={ !journeyData.selectedAdvisor }
                                onClick={ () => goToNextStep() }
                            />
                            <HelpButton phoneNumber={ csiPhoneNumberHelp } />
                        </ActionsLayout>
                    </Route>
                    <Route<TechNavigationPath> path="PreConfirmation" fallback={ LoaderComponent }>
                        <PreConfirmation
                            journeyData={ journeyData }
                            onSuccess={ (folder) => {
                                dispatchJourneyData({ type: 'SET_CONFIRMATION', folder });
                                goToStep(chooseEndStep(folder.lead?.funnel));
                            } }
                            onTechnicalError={ handleTechnicalError }
                        />
                        { LoaderComponent }
                    </Route>
                    <Route<NavigationPath> path="End/*" fallback={ LoaderComponent }>
                        <End
                            current={ step }
                            options={ _options }
                            journeyData={ journeyData }
                        />
                    </Route>
                    <Route<StepNavigationPath> path="Error" fallback={ LoaderComponent }>
                        <TechnicalError />
                    </Route>
                    <Route<TechNavigationPath> path="Scenarios" fallback={ LoaderComponent }>
                        <Scenarios
                            data={ journeyDataMocks }
                            onChange={ onScenarioChange }
                            onReady={ (dataMock?: JourneyData) => {
                                if (dataMock) {
                                    dispatchJourneyData({
                                        type: 'folder',
                                        payload: dataMock,
                                    });
                                }
                                goToStep(initialStep);
                            } }
                        />
                    </Route>
                </Router>
            </section>
        </MainLayout>
    );
}

export default App;
